import { useMemo, useRef, useState } from "react";
import {
  Package,
  PenLine,
  Search,
  ShieldCheck,
  Sparkles,
  Truck,
  X,
} from "lucide-react";
import { toast } from "sonner";

import {
  type CarrierAddOnServiceType,
  type CarrierProviderAddOnService,
  useCarrierProviderAddOnServices,
  useUpdateCarrierAddOnService,
} from "@/api/carrier";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  AddOnServiceCatalogue,
  type AddOnProviderGroup,
  type AddOnTypeConfig,
} from "@/features/add-on-service-catalogue";
import { AddOnDetailDialog } from "@/features/add-on-detail-dialog";
import { RenameAddOnServiceDialog } from "@/features/rename-add-on-service-dialog";
import { formatAddOnDetailSummary } from "@/lib/add-on-service-format";
import { hasHttpStatus } from "@/lib/api-error";

const TYPE_CONFIG: Record<CarrierAddOnServiceType, AddOnTypeConfig> = {
  PICKUP: {
    type: "PICKUP",
    title: "Pickup Services",
    shortLabel: "Pickup services",
    description:
      "Pickup service tiers granted to your carrier, organized by provider. Configure owned pickup services from the dedicated workspace.",
    emptyTitle: "No pickup services yet.",
    emptyDescription:
      "Services granted to this carrier appear here for pickup booking and pricing workflows.",
    Icon: Truck,
  },
  INSURANCE: {
    type: "INSURANCE",
    title: "Insurance Services",
    shortLabel: "Insurance services",
    description:
      "Insurance service tiers granted to your carrier, organized by provider. Coverage and fee settings come from the admin catalogue.",
    emptyTitle: "No insurance services yet.",
    emptyDescription:
      "Insurance services granted to this carrier appear here when available in pricing.",
    Icon: ShieldCheck,
  },
  PACKAGING: {
    type: "PACKAGING",
    title: "Packaging Services",
    shortLabel: "Packaging services",
    description:
      "Packaging service tiers granted to your carrier, organized by provider. Pricing models come from the admin catalogue.",
    emptyTitle: "No packaging services yet.",
    emptyDescription:
      "Packaging services granted to this carrier appear here when available in pricing.",
    Icon: Package,
  },
  SIGNATURE: {
    type: "SIGNATURE",
    title: "Signature Services",
    shortLabel: "Signature services",
    description:
      "Signature service tiers granted to your carrier, organized by provider. Flat-fee pricing comes from the admin catalogue.",
    emptyTitle: "No signature services yet.",
    emptyDescription:
      "Signature services granted to this carrier appear here when available in pricing.",
    Icon: PenLine,
  },
  SPECIAL_HANDLING: {
    type: "SPECIAL_HANDLING",
    title: "Special Handling Services",
    shortLabel: "Special handling services",
    description:
      "Special-handling service tiers granted to your carrier, organized by provider. Fees and handling notes come from the admin catalogue.",
    emptyTitle: "No special handling services yet.",
    emptyDescription:
      "Special-handling services granted to this carrier appear here when available in pricing.",
    Icon: Sparkles,
  },
};

interface AddOnServicesScreenProps {
  typeFilter?: CarrierAddOnServiceType;
  title?: string;
}

export default function AddOnServicesScreen({
  typeFilter = "PICKUP",
  title,
}: AddOnServicesScreenProps) {
  const config = TYPE_CONFIG[typeFilter];
  const addOns = useCarrierProviderAddOnServices({
    type: typeFilter,
    all: true,
  });
  const update = useUpdateCarrierAddOnService();
  const [search, setSearch] = useState("");
  const [activeGroup, setActiveGroup] = useState<string | undefined>();
  const [detail, setDetail] = useState<CarrierProviderAddOnService | null>(
    null
  );
  const [renaming, setRenaming] = useState<CarrierProviderAddOnService | null>(
    null
  );
  const [mutationTarget, setMutationTarget] = useState<{
    id: string;
    action: "rename" | "toggle";
  } | null>(null);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const mutationLock = useRef(false);
  const addOnRows: CarrierProviderAddOnService[] = Array.isArray(addOns.data)
    ? addOns.data
    : (addOns.data?.data ?? []);

  const allGroups = useMemo(() => groupServices(addOnRows), [addOnRows]);
  const groups = useMemo(
    () => groupServices(filterServices(addOnRows, search)),
    [addOnRows, search]
  );
  const currentGroupId = groups.some(
    (group) => group.providerId === activeGroup
  )
    ? activeGroup
    : groups[0]?.providerId;
  const queryForbidden = hasHttpStatus(addOns.error, 403);
  const queryError =
    addOns.error && (addOnRows.length === 0 || queryForbidden)
      ? addOns.error
      : null;

  const handleToggle = async (service: CarrierProviderAddOnService) => {
    if (mutationLock.current) return;
    mutationLock.current = true;
    setMutationError(null);
    setMutationTarget({ id: service.id, action: "toggle" });
    try {
      await update.mutateAsync({
        id: service.id,
        patch: { active: !service.active },
      });
      toast.success(service.active ? "Add-on disabled." : "Add-on enabled.");
    } catch (caught) {
      const message = mutationMessage(
        caught,
        "Add-on status could not be updated."
      );
      setMutationError(message);
      toast.error(message);
    } finally {
      mutationLock.current = false;
      setMutationTarget(null);
    }
  };

  const handleRename = async (
    service: CarrierProviderAddOnService,
    name: string
  ): Promise<boolean> => {
    if (mutationLock.current) return false;
    mutationLock.current = true;
    setMutationError(null);
    setMutationTarget({ id: service.id, action: "rename" });
    try {
      await update.mutateAsync({ id: service.id, patch: { name } });
      toast.success("Add-on renamed.");
      return true;
    } catch (caught) {
      const message = mutationMessage(caught, "Add-on could not be renamed.");
      setMutationError(message);
      toast.error(message);
      return false;
    } finally {
      mutationLock.current = false;
      setMutationTarget(null);
    }
  };

  return (
    <div className="carrier-page">
      <PageHeader title={title ?? config.title} />

      <Card className="overflow-hidden">
        <CardHeader className="flex-col items-stretch gap-3 space-y-0 border-b p-3 sm:flex-row sm:items-center sm:justify-between md:px-4 md:py-3">
          <CardTitle className="text-sm">
            {addOns.isLoading && addOnRows.length === 0
              ? "Loading services…"
              : `${addOnRows.length} service${addOnRows.length === 1 ? "" : "s"} across ${allGroups.length} provider carrier${allGroups.length === 1 ? "" : "s"}`}
          </CardTitle>
          <div className="relative w-full sm:w-72">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              aria-label={`Search ${config.shortLabel.toLowerCase()}`}
              placeholder="Search name, code, or provider"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="pl-9 pr-10"
            />
            {search && (
              <Button
                variant="ghost"
                size="icon-sm-touch"
                aria-label="Clear search"
                className="absolute right-0 top-1/2 -translate-y-1/2"
                onClick={() => setSearch("")}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-3">
          {addOns.error && addOnRows.length > 0 && !queryForbidden && (
            <p
              role="alert"
              className="mb-3 rounded-md border bg-destructive/5 p-3 text-sm"
            >
              Services could not be refreshed. Loaded catalogue data remains
              available.
            </p>
          )}
          <AddOnServiceCatalogue
            config={config}
            groups={groups}
            totalCount={addOnRows.length}
            currentGroupId={currentGroupId}
            search={search}
            isLoading={addOns.isLoading && addOnRows.length === 0}
            error={queryError}
            mutationTarget={mutationTarget}
            mutationError={renaming ? null : mutationError}
            onRetry={() => void addOns.refetch()}
            onGroupChange={setActiveGroup}
            onView={setDetail}
            onRename={(service) => {
              setMutationError(null);
              setRenaming(service);
            }}
            onToggle={(service) => void handleToggle(service)}
          />
        </CardContent>
      </Card>

      {detail && (
        <AddOnDetailDialog
          open
          onOpenChange={(open) => {
            if (!open) setDetail(null);
          }}
          service={detail}
        />
      )}
      {renaming && (
        <RenameAddOnServiceDialog
          key={renaming.id}
          service={renaming}
          pending={
            mutationTarget?.id === renaming.id &&
            mutationTarget.action === "rename"
          }
          error={mutationError}
          onClose={() => {
            setRenaming(null);
            setMutationError(null);
          }}
          onSave={(name) => handleRename(renaming, name)}
        />
      )}
    </div>
  );
}

function filterServices(
  services: CarrierProviderAddOnService[],
  search: string
): CarrierProviderAddOnService[] {
  const needle = search.trim().toLocaleLowerCase();
  if (!needle) return services;
  return services.filter((service) =>
    [
      service.name,
      service.code,
      service.provider.name,
      service.provider.code,
      formatAddOnDetailSummary(service, 10),
      ...service.labelCarrierAccess.map((access) => access.labelCarrier.name),
    ].some((value) => value.toLocaleLowerCase().includes(needle))
  );
}

function groupServices(
  services: CarrierProviderAddOnService[]
): AddOnProviderGroup[] {
  const byProvider = new Map<string, CarrierProviderAddOnService[]>();
  for (const service of services) {
    const rows = byProvider.get(service.provider.id) ?? [];
    rows.push(service);
    byProvider.set(service.provider.id, rows);
  }
  return [...byProvider.entries()]
    .map(([providerId, rows]) => ({
      providerId,
      name: rows[0]?.provider.name ?? providerId,
      services: rows.sort((a, b) => a.name.localeCompare(b.name)),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

function mutationMessage(error: unknown, fallback: string): string {
  if (hasHttpStatus(error, 403)) {
    return "You do not have permission to manage this add-on service.";
  }
  const response = error as { response?: { data?: { error?: string } } };
  return response.response?.data?.error ?? fallback;
}
